/** @owner @Appboy/ai-decisioning-experience */

import { useState } from "react";

import type { DecisioningCreativeComponentSelections } from "../types";
import { emptyDecisioningCreativeComponentSelections } from "./decisioningCreativeComponentLiquidContextConstants";

/**
 * Owns DS preview selections and resets them when the used-components set or default
 * variant options change.
 *
 * @param usedComponentsKey - Stable key for the sorted used-component names (e.g. `join(",")`).
 * @param defaultSelections - First-variant defaults for the current used components.
 * @return Current selections and setter for preview overrides.
 */
export const useDecisioningCreativeComponentSelections = (
  usedComponentsKey: string,
  defaultSelections: DecisioningCreativeComponentSelections = emptyDecisioningCreativeComponentSelections
) => {
  const [selections, setSelections] = useState<DecisioningCreativeComponentSelections>(defaultSelections);
  const [prevUsedComponentsKey, setPrevUsedComponentsKey] = useState(usedComponentsKey);
  const [prevDefaultSelectionsKey, setPrevDefaultSelectionsKey] = useState(() => JSON.stringify(defaultSelections));

  const defaultSelectionsKey = JSON.stringify(defaultSelections);

  if (usedComponentsKey !== prevUsedComponentsKey) {
    setPrevUsedComponentsKey(usedComponentsKey);
    setPrevDefaultSelectionsKey(defaultSelectionsKey);
    setSelections(defaultSelections);
  } else if (defaultSelectionsKey !== prevDefaultSelectionsKey) {
    setPrevDefaultSelectionsKey(defaultSelectionsKey);
    setSelections(defaultSelections);
  }

  return { selections, setSelections };
};
